import { Grid, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, CircularProgress, Card, Tooltip, FormControlLabel, Switch, Chip, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import FormField from "layouts/pages/users/new-user/components/FormField";
import { useEffect, useRef, useState } from "react";
import { Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { useUser } from "context/UserContext";
import { useMaterialUIController } from "context";
import MDSelect from "components/MDSelect";
import SeletorMarcacoes from "./SeletorMarcacoes";
import DevedorEmailSection from "./DevedorEmailSection";
import DevedorTelefoneSection from "./DevedorTelefoneSection";
import DevedorEnderecoSection from "./DevedorEnderecoSection";
import { get, post, postNoResponse, postUploadNoResponse } from "utils/WebService";
import MDSnackbar from "components/MDSnackbar";
import { successAlert } from "utils/Functions";
import { TIPO_PERFIL, temTipoPerfil } from "utils/Perfis";

const validationSchema = Yup.object().shape({
    nomeDevedor: Yup.string().required("Campo obrigatório"),
    codigoCliente: Yup.string().required("Campo obrigatório"),
    idPessoa: Yup.string().required("Selecione um cliente"),
    emails: Yup.array().of(
        Yup.object().shape({
            ccEmail: Yup.string().test('is-valid-email', 'E-mail inválido', value => {
                if (!value) return true;
                const strEmail = String(value).toLowerCase();
                const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!re.test(strEmail)) return false;
                if (strEmail.endsWith("@gamil.com")) return false;
                if (strEmail.endsWith("@hotmai.com") || strEmail.endsWith("@hotamil.com") || strEmail.endsWith("@outlok.com")) return false;
                return true;
            })
        })
    )
});

const LISTAS_CONTATO = ["emails", "telefones", "enderecos"];

// Ids dos contatos que o formulário recebeu do servidor, por lista
const idsDosContatos = (dados) => Object.fromEntries(LISTAS_CONTATO.map(lista =>
    [lista, new Set((dados?.[lista] || []).map(c => c.id).filter(Boolean))]));

// Valores do formulário a partir do /devedor/by-id
const valoresDoDevedor = (data) => ({
    id: data.id,
    idPessoa: data.idPessoa,
    nomeDevedor: data.nome || '',
    cpfCnpj: data.documento || '',
    codigoCliente: data.codigoCliente || '',
    // Segmento e unidade do devedor no cliente (UMC: vão na Prestação de Contas); a tela só mostra quando preenchidos
    segmento: data.segmento || '',
    unidade: data.unidade || '',
    temSegmentoUnidade: Boolean(data.segmento || data.unidade),
    idUsuario: data.idUsuario,
    status: data.idStatusFicha,
    emails: data.emails || [],
    enderecos: data.enderecos || [],
    telefones: data.telefones || [],
    marcacoesIds: data.marcacoesIds || [],
    observacao: data.observacao || '',
    fichas: data.fichas || []
});

export default function CadastroBasico({ formRef, recordId, isJuridico, closeCreateEdit, isViewOnly, pendingContratos }) {
    const { processoStatus, userPerfils, userFunctionalities } = useUser();
    const isAdminOrSuporte = temTipoPerfil(userPerfils,
        TIPO_PERFIL.GESTOR, TIPO_PERFIL.COORDENADOR, TIPO_PERFIL.ADMINISTRADOR, TIPO_PERFIL.SUPORTE);
    
    const canCreate = userFunctionalities && userFunctionalities.includes("c050_C");
    const canEdit = userFunctionalities && userFunctionalities.includes("c050_U");
    const hasPermissionToEdit = recordId ? (canEdit || isAdminOrSuporte) : (canCreate || canEdit || isAdminOrSuporte);

    const disableFields = isViewOnly || !hasPermissionToEdit;
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const [apenasValidos, setApenasValidos] = useState(true);
    const [codigoDiferente, setCodigoDiferente] = useState(false);
    // Códigos que o devedor já teve no cliente (ex.: trocado na migração para o Jurídico); a busca também acha por eles
    const [codigosAnteriores, setCodigosAnteriores] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [statusFicha, setStatusFicha] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [erroSalvar, setErroSalvar] = useState("");
    const idsCarregados = useRef(idsDosContatos(null));
    const recarregar = useRef(null);
    const [openDevolverModal, setOpenDevolverModal] = useState(false);
    const [devolverObs, setDevolverObs] = useState("");
    const [devolverFile, setDevolverFile] = useState(null);
    const [isSubmittingDevolucao, setIsSubmittingDevolucao] = useState(false);
    const [confirmAction, setConfirmAction] = useState({ open: false, title: "", message: "", onConfirm: null });

    useEffect(() => {
        if (!recordId) {
            setCodigosAnteriores([]);
            return undefined;
        }
        let ativo = true;
        const carregar = () => get(`/devedor/codigo-cliente/${recordId}`)
            .then(res => { if (ativo) setCodigosAnteriores(res?.codigosAnteriores || []); })
            .catch(() => { if (ativo) setCodigosAnteriores([]); });
        carregar();
        // O código muda na migração para o Jurídico e no próprio Salvar Cadastro: relê junto com a ficha
        window.addEventListener("devedorStatusUpdated", carregar);
        return () => {
            ativo = false;
            window.removeEventListener("devedorStatusUpdated", carregar);
        };
    }, [recordId]);

    useEffect(() => {
        get("/pessoa/list-all").then(({ list }) => setClientes(list || [])).catch(() => {});

        get(`/users/list-all-operators`)
            .then((res) => {
                const lista = res.list || res.data || (Array.isArray(res) ? res : []);
                // Mantém o operador inativo da ficha, se ele já foi incluído pelo carregamento do devedor
                setUsuarios(prev => [...lista, ...prev.filter(p => !lista.some(u => u.id === p.id))]);
            }).catch(() => {
                setUsuarios([]);
            });

        get("/status-ficha/list-all").then((res) => {
            let list = res.list || res.data || (Array.isArray(res) ? res : []);
            setStatusFicha(list.sort((a, b) => String(a.ccNome || a.nome || "").localeCompare(String(b.ccNome || b.nome || ""))));
        }).catch(() => {
            setStatusFicha([]);
        });

    }, []);

    return (
        <MDBox pb={3}>
            <Formik
                innerRef={formRef}
                initialValues={{
                    id: recordId || null,
                    idPessoa: null,
                    nomeDevedor: '',
                    cpfCnpj: '',
                    codigoCliente: '',
                    idUsuario: null,
                    status: null,
                    emails: [],
                    enderecos: [],
                    telefones: [],
                    marcacoesIds: [],
                    observacao: '',
                    fichas: []
                }}
                validationSchema={validationSchema}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                    const codFinal = (values.codigoCliente || '').trim() || (values.cpfCnpj || '').trim();
                    if (!codFinal) {
                        alert("O campo Código do Cliente não pode ser vazio!");
                        setSubmitting(false);
                        return;
                    }

                    const cleanTelefones = (tels) => (tels || []).map(t => ({
                        ...t,
                        ccDdd: t.ccDdd ? String(t.ccDdd).replace(/\D/g, "") : null,
                        ccTelefone: t.ccTelefone ? String(t.ccTelefone).replace(/\D/g, "") : null
                    }));

                    if (!values.id && (!pendingContratos || pendingContratos.length === 0)) {
                        alert("Não é possível salvar o devedor sem cadastrar um débito/contrato. Vá até a aba Contratos e adicione ao menos um.");
                        setSubmitting(false);
                        return;
                    }

                    setErrorSB(false);
                    setSuccessSB(false);

                    try {
                        const contatos = { emails: values.emails, telefones: values.telefones, enderecos: values.enderecos };
                        if (values.id) {
                            // O backend apaga os contatos que não vêm na lista. Os incluídos depois que a ficha abriu
                            // (telefone/e-mail informado numa ocorrência, outro usuário) não estão no formulário: vão junto para não sumirem.
                            const atual = await get(`/devedor/by-id/${values.id}`);
                            LISTAS_CONTATO.forEach(lista => {
                                const novos = (atual?.[lista] || []).filter(c => c.id && !idsCarregados.current[lista].has(c.id));
                                contatos[lista] = [...(contatos[lista] || []), ...novos];
                            });
                        }

                        const payload = {
                            id: values.id,
                            idPessoa: values.idPessoa,
                            nome: values.nomeDevedor,
                            documento: values.cpfCnpj,
                            codigoCliente: codFinal,
                            ...(values.temSegmentoUnidade ? { segmento: values.segmento, unidade: values.unidade } : {}),
                            emails: contatos.emails,
                            enderecos: contatos.enderecos,
                            telefones: cleanTelefones(contatos.telefones),
                            contratos: pendingContratos || []
                        };
                        // Status, propriedade, marcações e observação só na inclusão: na ficha existente são da aba Dados da Carteira
                        if (!values.id) {
                            Object.assign(payload, {
                                idUsuario: values.idUsuario,
                                idStatusFicha: values.status,
                                marcacoesIds: values.marcacoesIds,
                                observacao: values.observacao
                            });
                        }

                        await postNoResponse("/devedor/save", payload);
                        setSuccessSB(true);
                        resetForm({ values });
                        if (values.id) {
                            // Recarrega com os ids que o servidor deu aos contatos novos (sem eles, salvar de novo os duplicava)
                            window.dispatchEvent(new Event("devedorStatusUpdated"));
                            if (recarregar.current) recarregar.current();
                        } else if (closeCreateEdit) {
                            successAlert("Ficha do devedor salva com sucesso!", closeCreateEdit);
                        }
                    } catch (error) {
                        console.error("Erro ao salvar devedor", error);
                        setErroSalvar(error?.message || "");
                        setErrorSB(true);
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ values, initialValues, handleChange, setFieldValue, errors, touched, isSubmitting, dirty }) => {
                    const isEmailsDirty = JSON.stringify(values.emails) !== JSON.stringify(initialValues.emails);
                    const isTelefonesDirty = JSON.stringify(values.telefones) !== JSON.stringify(initialValues.telefones);
                    const isEnderecosDirty = JSON.stringify(values.enderecos) !== JSON.stringify(initialValues.enderecos);
                    return (
                    <Form>
                        {/* Dummy inputs to intercept Chrome autofill */}
                        <input type="text" name="dummy_username" style={{ display: 'none' }} aria-hidden="true" autoComplete="username" />
                        <input type="password" name="dummy_password" style={{ display: 'none' }} aria-hidden="true" autoComplete="current-password" />
                        <FetchAndSetData recordId={recordId} setUsuarios={setUsuarios} setCodigoDiferente={setCodigoDiferente} idsCarregados={idsCarregados} recarregar={recarregar} />
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Cliente</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.nomeComum}
                                    name="idPessoa"
                                    placeholder="Selecione..."
                                    value={clientes.find(c => c.id === values.idPessoa) || null}
                                    options={clientes}
                                    onChange={(value) => {
                                        setFieldValue("idPessoa", value ? value.id : null);
                                    }}
                                    isDisabled={disableFields}
                                />
                                {errors.idPessoa && touched.idPessoa && (
                                    <MDTypography variant="caption" color="error">{errors.idPessoa}</MDTypography>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormField
                                    type="text"
                                    label="Nome"
                                    name="nomeDevedor"
                                    value={values.nomeDevedor}
                                    onChange={handleChange}
                                    onBlur={(e) => {
                                        if (e.target.value) {
                                            post("/devedor/list", { page: 0, pageSize: 1, ccNome: e.target.value }).then(res => {
                                                if (res.data && res.data.length > 0) {
                                                    if (res.data.some(d => d.id !== recordId)) alert("Já existe um devedor com este Nome na base!");
                                                }
                                            }).catch(() => {});
                                        }
                                    }}
                                    placeholder="João da Silva"
                                    error={errors.nomeDevedor && touched.nomeDevedor}
                                    InputProps={{ readOnly: disableFields }}
                                    autoComplete="off"
                                    style={{ "& .MuiInputBase-root": { height: "40px", minHeight: "40px" } }}
                                />
                                {errors.nomeDevedor && touched.nomeDevedor && (
                                    <MDTypography variant="caption" color="error">{errors.nomeDevedor}</MDTypography>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={2.5}>
                                <FormField
                                    type="text"
                                    label="CPF/CNPJ"
                                    name="cpfCnpj"
                                    value={values.cpfCnpj}
                                    onChange={(e) => {
                                        handleChange(e);
                                        if (!codigoDiferente) {
                                            setFieldValue("codigoCliente", e.target.value);
                                        }
                                    }}
                                    onBlur={(e) => {
                                        if (e.target.value) {
                                            post("/devedor/list", { page: 0, pageSize: 1, ccDocumento: e.target.value }).then(res => {
                                                if (res.data && res.data.length > 0) {
                                                    if (res.data.some(d => d.id !== recordId)) alert("Já existe um devedor com este CPF/CNPJ na base!");
                                                }
                                            }).catch(() => {});
                                        }
                                    }}
                                    placeholder="Informe CPF ou CNPJ"
                                    InputProps={{ readOnly: disableFields }}
                                    style={{ "& .MuiInputBase-root": { height: "40px", minHeight: "40px" } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2.5}>
                                <MDBox position="relative">
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Cód. Cliente *</InputLabel>
                                    {!disableFields && (
                                        <Tooltip title={codigoDiferente ? "Voltar a copiar automaticamente o CPF/CNPJ" : "Clique para digitar um Código diferente do Documento"}>
                                            <Box
                                                component="span"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    const next = !codigoDiferente;
                                                    setCodigoDiferente(next);
                                                    if (!next) {
                                                        setFieldValue("codigoCliente", values.cpfCnpj || "");
                                                    }
                                                }}
                                                sx={{
                                                    position: "absolute",
                                                    top: "2px",
                                                    right: "8px",
                                                    zIndex: 2,
                                                    cursor: "pointer",
                                                    fontSize: "0.65rem",
                                                    lineHeight: 1,
                                                    py: "2px",
                                                    px: "6px",
                                                    borderRadius: "4px",
                                                    border: "1px solid",
                                                    borderColor: codigoDiferente ? "#fb8c00" : "#00b0ff",
                                                    backgroundColor: codigoDiferente ? "#fb8c00" : "transparent",
                                                    color: codigoDiferente ? "#fff !important" : "#00b0ff !important",
                                                    fontWeight: "bold",
                                                    userSelect: "none",
                                                    transition: "all 150ms ease-in-out",
                                                    "&:hover": {
                                                        opacity: 0.85
                                                    }
                                                }}
                                            >
                                                {codigoDiferente ? "Diferente" : "Diferente?"}
                                            </Box>
                                        </Tooltip>
                                    )}
                                    <MDInput
                                        name="codigoCliente"
                                        value={values.codigoCliente}
                                        onChange={handleChange}
                                        onBlur={(e) => {
                                            const cod = (e.target.value || "").trim();
                                            if (cod && values.idPessoa) {
                                                post("/devedor/list", { 
                                                    page: 0, 
                                                    pageSize: 1, 
                                                    ccCodigoCliente: cod,
                                                    idPessoa: values.idPessoa
                                                }).then(res => {
                                                    if (res.data && res.data.length > 0) {
                                                        if (res.data.some(d => d.id !== recordId)) {
                                                            alert("Atenção: Já existe uma ficha/devedor cadastrado com este Código para o Cliente selecionado!");
                                                        }
                                                    }
                                                }).catch(() => {});
                                            }
                                        }}
                                        placeholder={codigoDiferente ? "Informe código único" : (values.cpfCnpj || "Espelho CPF/CNPJ")}
                                        error={Boolean(errors.codigoCliente && touched.codigoCliente)}
                                        size="small"
                                        fullWidth
                                        InputProps={{
                                            readOnly: disableFields || !codigoDiferente,
                                            sx: !codigoDiferente ? { cursor: "not-allowed" } : {}
                                        }}
                                       
                                    />
                                    {errors.codigoCliente && touched.codigoCliente && (
                                        <MDBox mt={0.5}>
                                            <MDTypography variant="caption" color="error">{errors.codigoCliente}</MDTypography>
                                        </MDBox>
                                    )}
                                    {codigosAnteriores.length > 0 && (
                                        <Tooltip
                                            placement="bottom-start"
                                            title={
                                                <MDBox>
                                                    {codigosAnteriores.map((c, i) => (
                                                        <MDTypography key={i} variant="caption" display="block" sx={{ color: "inherit" }}>
                                                            {c.codigo} até {c.ate}{c.motivo ? ` · ${c.motivo}` : ""}{c.usuario ? ` · ${c.usuario}` : ""}
                                                        </MDTypography>
                                                    ))}
                                                </MDBox>
                                            }
                                        >
                                            <MDTypography variant="caption" color="text" display="block" px={1} mt={0.5} sx={{ cursor: "help" }}>
                                                Código anterior: {codigosAnteriores[0].codigo} (até {codigosAnteriores[0].ate})
                                                {codigosAnteriores.length > 1 ? ` e mais ${codigosAnteriores.length - 1}` : ""}
                                            </MDTypography>
                                        </Tooltip>
                                    )}
                                </MDBox>
                            </Grid>
                            {values.temSegmentoUnidade && (
                                <>
                                    <Grid item xs={12} sm={3}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Segmento</InputLabel>
                                        <MDInput name="segmento" value={values.segmento} onChange={handleChange} size="small" fullWidth
                                            inputProps={{ maxLength: 40 }} InputProps={{ readOnly: disableFields }} />
                                        <MDTypography variant="caption" color="text" px={1}>
                                            UMCTEC sai como SOMEC na Prestação de Contas; o resto, OMEC
                                        </MDTypography>
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Unidade / Campus</InputLabel>
                                        <MDInput name="unidade" value={values.unidade} onChange={handleChange} size="small" fullWidth
                                            inputProps={{ maxLength: 20 }} InputProps={{ readOnly: disableFields }} />
                                    </Grid>
                                </>
                            )}
                            {!recordId && (
                                <>
                                    <Grid item xs={12} sm={3}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Propriedade</InputLabel>
                                        <MDSelect
                                            isMulti={false}
                                            getOptionValue={(option) => option.id}
                                            getOptionLabel={(option) => option.nome}
                                            name="idUsuario"
                                            placeholder="Selecione..."
                                            value={usuarios.find(u => u.id === values.idUsuario) || null}
                                            options={usuarios}
                                            onChange={(value) => {
                                                setFieldValue("idUsuario", value ? value.id : null);
                                            }}
                                            isDisabled={disableFields}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Status</InputLabel>
                                        <MDSelect
                                            isMulti={false}
                                            getOptionValue={(option) => option.id}
                                            getOptionLabel={(option) => option.ccNome}
                                            name="status"
                                            placeholder="Selecione..."
                                            value={statusFicha.find(s => s.id === values.status) || null}
                                            options={statusFicha}
                                            onChange={(value) => {
                                                setFieldValue("status", value ? value.id : null);
                                            }}
                                            isDisabled={false}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={5}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Marcação Especial</InputLabel>
                                        {/* Na inclusão, a carteira é a do contrato incluído (a ficha nasce dela) */}
                                        <SeletorMarcacoes
                                            carteira={!recordId ? (pendingContratos || []).map(c => c.idCarteira || c.carteiraId).find(Boolean) : undefined}
                                            alvos={[1, 3]}
                                            name="marcacoesIds"
                                            value={values.marcacoesIds}
                                            onChange={(ids) => setFieldValue("marcacoesIds", ids)}
                                            isDisabled={disableFields}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormField
                                            type="text"
                                            label="Observação"
                                            name="observacao"
                                            value={values.observacao}
                                            onChange={handleChange}
                                            placeholder="Digite suas observações..."
                                            multiline
                                            rows={3}
                                        />
                                    </Grid>
                                </>
                            )}

                            {/* Flag de Filtro de Contatos Válidos */}
                            <Grid item xs={12}>
                                <MDBox display="flex" alignItems="center" mt={1} mb={0.5}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={apenasValidos}
                                                onChange={(e) => setApenasValidos(e.target.checked)}
                                                color="info"
                                            />
                                        }
                                        label={
                                            <MDTypography variant="button" fontWeight="regular" color="text" sx={{ cursor: "pointer", userSelect: "none", ml: -0.5 }}>
                                                Apenas contatos válidos
                                            </MDTypography>
                                        }
                                        sx={{ margin: 0 }}
                                    />
                                </MDBox>
                            </Grid>

                            <Grid item xs={12}>
                                <MDBox>
                                    <DevedorEmailSection values={values} setFieldValue={setFieldValue} handleChange={handleChange} errors={errors} touched={touched} isViewOnly={false} apenasValidos={apenasValidos} />
                                </MDBox>
                            </Grid>

                            <Grid item xs={12}>
                                <MDBox mt={3}>
                                    <DevedorTelefoneSection values={values} setFieldValue={setFieldValue} handleChange={handleChange} isViewOnly={false} apenasValidos={apenasValidos} />
                                </MDBox>
                            </Grid>

                            <Grid item xs={12}>
                                <MDBox mt={3}>
                                    <DevedorEnderecoSection values={values} setFieldValue={setFieldValue} handleChange={handleChange} isViewOnly={false} apenasValidos={apenasValidos} />
                                </MDBox>
                            </Grid>
                            
                            <Grid item xs={12} display="flex" justifyContent="flex-end" mt={2}>
                                <MDButton variant="gradient" color="info" type="submit" disabled={!dirty || isSubmitting}>
                                    Salvar Cadastro
                                </MDButton>
                            </Grid>
                        </Grid>
                    </Form>
                    );
                }}
            </Formik>
            <MDSnackbar
                color="success"
                icon="check"
                title="Sucesso"
                content="Ficha do devedor salva com sucesso!"
                dateTime=""
                open={successSB}
                onClose={() => setSuccessSB(false)}
                close={() => setSuccessSB(false)}
                bgWhite
            />
            <MDSnackbar
                color="error"
                icon="warning"
                title="Erro"
                content={erroSalvar || "Ocorreu um erro ao salvar o devedor."}
                dateTime=""
                open={errorSB}
                onClose={() => setErrorSB(false)}
                close={() => setErrorSB(false)}
                bgWhite
            />
        </MDBox>
    );
}

// Carrega o devedor só quando muda a ficha (antes recarregava também quando a lista de operadores chegava,
// apagando o que o usuário já tinha digitado) e quando outra aba altera a ficha, se não há edição em andamento.
const FetchAndSetData = ({ recordId, setUsuarios, setCodigoDiferente, idsCarregados, recarregar }) => {
    const { resetForm, dirty } = useFormikContext();
    const editando = useRef(dirty);
    editando.current = dirty;

    useEffect(() => {
        if (!recordId) return undefined;
        let ativo = true;
        const carregar = () => get(`/devedor/by-id/${recordId}`).then(data => {
            if (!ativo || !data) return;
            if (data.idUsuario) {
                setUsuarios(prev => prev.find(u => u.id === data.idUsuario)
                    ? prev
                    : [...prev, { id: data.idUsuario, nome: data.nomeUsuario || "Operador Sem Acesso / Inativo" }]);
            }
            const doc = data.documento || '';
            const cod = data.codigoCliente || '';
            if (setCodigoDiferente) {
                setCodigoDiferente(Boolean(cod && doc && cod.trim() !== doc.trim()));
            }
            idsCarregados.current = idsDosContatos(data);
            resetForm({ values: valoresDoDevedor(data) });
        }).catch(console.error);

        recarregar.current = carregar;
        carregar();
        const aoAtualizarFicha = () => { if (!editando.current) carregar(); };
        window.addEventListener("devedorStatusUpdated", aoAtualizarFicha);
        return () => {
            ativo = false;
            recarregar.current = null;
            window.removeEventListener("devedorStatusUpdated", aoAtualizarFicha);
        };
    }, [recordId, resetForm, setUsuarios, setCodigoDiferente, idsCarregados, recarregar]);

    return null;
};
